import { Building2, ShieldAlert, ShieldCheck, Loader2, Crown } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

const eur = (v) => (v == null ? "—" : `${Math.round(v / 1000).toLocaleString("fr-FR")} k€`);

export function GroupeHoldingBlock({ groupe: g }) {
    if (!g) return null;
    const stats = [
        { label: "CA total agrégé (sociétés actives)", value: eur(g.ca_total), hint: `connu sur ${g.ca_connu_sur}/${g.nb_actives} société${g.nb_actives > 1 ? "s" : ""}`, testId: "groupe-ca-total" },
        { label: "Sociétés du groupe", value: g.nb_societes, hint: `${g.nb_actives} active${g.nb_actives > 1 ? "s" : ""} · ${g.nb_cessees} cessée${g.nb_cessees > 1 ? "s" : ""}`, testId: "groupe-nb-societes" },
        { label: "Procédures collectives", value: g.nb_procedures, hint: g.nb_procedures ? `contamination groupe — ${g.nb_verifiees ?? 0}/${g.nb_societes} entités vérifiées` : `aucune détectée (${g.nb_verifiees ?? 0}/${g.nb_societes} entités vérifiées Pappers)`, testId: "groupe-nb-procedures", alert: g.nb_procedures > 0 },
    ];
    return (
        <Card data-testid="groupe-holding-card" className="border-slate-200 bg-white shadow-sm">
            <CardContent className="pt-6 space-y-5">
                <div className="flex items-center gap-3">
                    <div className="h-9 w-9 rounded-full bg-accent flex items-center justify-center">
                        <Building2 className="h-4 w-4 text-primary" strokeWidth={1.75} />
                    </div>
                    <div>
                        <h2 className="font-display text-xl text-foreground">Groupe &amp; Holding</h2>
                        <p className="text-xs text-muted-foreground">Périmètre reconstitué à partir des mandats des dirigeants (données Pappers, dernier exercice connu).</p>
                    </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {stats.map((s) => (
                        <div key={s.testId} data-testid={s.testId} className="rounded-md border border-border bg-background px-4 py-3">
                            <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground">{s.label}</p>
                            <p className={`mt-1 font-display text-2xl ${s.alert ? "text-rose-700" : "text-foreground"}`}>{s.value}</p>
                            <p className="text-xs text-muted-foreground">{s.hint}</p>
                        </div>
                    ))}
                </div>
                <div data-testid="groupe-holdings" className="flex flex-wrap items-center gap-2 text-sm">
                    <span className="text-muted-foreground">Holdings identifiées :</span>
                    {g.holdings.length === 0 ? (
                        <span className="text-muted-foreground">aucune (NAF 6420Z / 6430Z / 7010Z ou dénomination « holding »)</span>
                    ) : (
                        g.holdings.map((h) => (
                            <Badge key={h.siren} className="rounded-full bg-primary/10 text-primary border-transparent shadow-sm">
                                {h.nom_entreprise} · {eur(h.chiffre_affaires)}
                            </Badge>
                        ))
                    )}
                </div>
            </CardContent>
        </Card>
    );
}

export function MandatsProceduresTable({ groupe: g, checks, onVerify, onDefineHolding, holdingSiren }) {
    if (!g?.societes?.length) return null;
    return (
        <Card data-testid="mandats-procedures-card" className="border-slate-200 bg-white shadow-sm">
            <CardContent className="pt-6 space-y-4">
                <h2 className="font-display text-xl text-foreground">Mandats &amp; Procédures</h2>
                <table className="w-full text-sm">
                    <thead>
                        <tr className="text-left text-xs uppercase tracking-[0.14em] text-muted-foreground border-b border-border">
                            <th className="py-2 pr-3 font-medium">Société</th>
                            <th className="py-2 pr-3 font-medium">Dirigeant(s)</th>
                            <th className="py-2 pr-3 font-medium">Statut</th>
                            <th className="py-2 pr-3 font-medium text-right">CA</th>
                            <th className="py-2 pr-3 font-medium text-right">Procédure collective</th>
                            {onDefineHolding && <th className="py-2 font-medium text-right">Holding</th>}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                        {g.societes.map((s) => {
                            const c = checks[s.siren];
                            const known = s.procedure_en_cours || s.nb_procedures > 0 || (c && !c.loading && !c.error && (c.procedure_en_cours || c.procedures?.length > 0));
                            const verifiedClean = c && !c.loading && !c.error && !c.procedure_en_cours && !(c.procedures?.length > 0);
                            return (
                                <tr key={s.siren} data-testid={`groupe-row-${s.siren}`} className={known ? "bg-rose-50/60" : ""}>
                                    <td className="py-2.5 pr-3">
                                        <span className="font-medium text-foreground flex items-center gap-2">
                                            {known && <span data-testid={`proc-dot-${s.siren}`} className="inline-block h-2.5 w-2.5 rounded-full bg-rose-600 ring-4 ring-rose-100" aria-label="Procédure collective détectée" />}
                                            {s.nom_entreprise}
                                            {s.auditee && <Badge className="rounded-full bg-primary/10 text-primary border-transparent shadow-sm text-[10px]">Auditée</Badge>}
                                        </span>
                                        <p className="text-xs text-muted-foreground">{s.forme_juridique}{s.ville ? ` · ${s.ville}` : ""} · SIREN {s.siren}</p>
                                    </td>
                                    <td className="py-2.5 pr-3 text-muted-foreground">{s.dirigeants?.join(", ") || "—"}</td>
                                    <td className="py-2.5 pr-3">
                                        <span className={`text-xs font-medium ${s.entreprise_cessee ? "text-rose-700" : "text-emerald-700"}`}>{s.entreprise_cessee ? "Cessée" : s.statut_consolide || "Active"}</span>
                                    </td>
                                    <td className="py-2.5 pr-3 text-right tabular-nums">{eur(s.chiffre_affaires)}</td>
                                    <td className="py-2.5 pr-3 text-right">
                                        {known ? (
                                            <span data-testid={`groupe-proc-alert-${s.siren}`} className="inline-flex items-center gap-1 text-xs font-medium text-rose-700"><ShieldAlert className="h-3.5 w-3.5" /> Détectée</span>
                                        ) : c?.loading ? (
                                            <Loader2 className="h-4 w-4 animate-spin inline text-muted-foreground" />
                                        ) : verifiedClean ? (
                                            <span data-testid={`groupe-proc-ok-${s.siren}`} className="inline-flex items-center gap-1 text-xs text-emerald-700"><ShieldCheck className="h-3.5 w-3.5" /> Aucune</span>
                                        ) : s.auditee ? (
                                            <span className="text-xs text-emerald-700">Aucune</span>
                                        ) : (
                                            <button type="button" data-testid={`groupe-verify-${s.siren}`} onClick={() => onVerify(s.siren)} className="text-xs text-primary underline-offset-2 hover:underline">Vérifier</button>
                                        )}
                                    </td>
                                    {onDefineHolding && (
                                        <td className="py-2.5 text-right">
                                            {s.auditee ? null : holdingSiren === s.siren ? (
                                                <button type="button" data-testid={`holding-current-${s.siren}`} onClick={() => onDefineHolding(s)} title="Cliquer pour retirer" className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2.5 py-1 text-[11px] font-semibold text-primary"><Crown className="h-3 w-3" /> Société mère</button>
                                            ) : (
                                                <button type="button" data-testid={`holding-define-${s.siren}`} onClick={() => onDefineHolding(s)} className="text-xs text-muted-foreground underline-offset-2 hover:text-primary hover:underline">Définir comme Holding</button>
                                            )}
                                        </td>
                                    )}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </CardContent>
        </Card>
    );
}
